import React from 'react';
import { Modal, Select, Switch, Text, TextInput } from '@mantine/core';
import type { PrinterView } from 'shared-models/browser';
import { Button } from '../../../../components/Button/Button';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import classes from './stationary-setup-modal.module.scss';

type StationarySetupModalProps = {
  opened: boolean;
  isSaving: boolean;
  value: string | null;
  drawers?: PrinterView[];
  deviceId?: string;
  onClose: () => void;
  onSave: (name: string | null, ownershipChange?: { printerId: number; stationName: string | null; takeOwnership?: true }) => void;
};

const NO_SELECTION_INPUT_PROPS = {
  onContextMenu: (event: React.MouseEvent<HTMLInputElement>) => event.preventDefault(),
  onSelect: (event: React.SyntheticEvent<HTMLInputElement>) => event.preventDefault(),
};

export const StationarySetupModal: React.FC<StationarySetupModalProps> = ({
  opened,
  isSaving,
  value,
  drawers = [],
  deviceId,
  onClose,
  onSave,
}) => {
  const [name, setName] = React.useState(value ?? '');
  const [selectedDrawerId, setSelectedDrawerId] = React.useState<string | null>(null);
  const [ownershipRequested, setOwnershipRequested] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (!opened) {
      return;
    }
    setName(value ?? '');
    setSelectedDrawerId(null);
    setOwnershipRequested(null);
  }, [opened, value]);

  const normalized = name.trim();
  const selectedDrawer = drawers.find((drawer) => String(drawer.id) === selectedDrawerId)
    ?? (drawers.length === 1 ? drawers[0] : undefined);
  const ownsDrawer = Boolean(deviceId && selectedDrawer?.cashDrawerOwnerDeviceId === deviceId);
  const wantsOwnership = ownershipRequested ?? ownsDrawer;
  const ownershipDirty = Boolean(selectedDrawer && (
    wantsOwnership !== ownsDrawer
    || (ownsDrawer && selectedDrawer.cashDrawerOwnerStationName !== normalized)
  ));
  const isDirty = (value ?? '') !== normalized || ownershipDirty;

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!isSaving) {
          onClose();
        }
      }}
      onContextMenu={(event) => event.preventDefault()}
      title={<span className={classes.title}>הגדר עמדה נייחת</span>}
      size="md"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
      closeButtonProps={{ 'data-testid': 'stationary-setup-close' } as Record<string, string>}
    >
      <div className={classes.body} dir="rtl">
        <TextInput
          data-testid="stationary-setup-input"
          label="שם עמדה"
          placeholder="לדוגמה: עמדה ראשית"
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          maxLength={50}
          disabled={isSaving}
          {...NO_SELECTION_INPUT_PROPS}
        />

        <p className={classes.hint}>השאר ריק כדי לבטל הגדרת עמדה נייחת.</p>

        {drawers.length > 0 && (
          <>
            <Select
              label="מגירת מזומנים"
              placeholder="בחר מגירה"
              value={selectedDrawer ? String(selectedDrawer.id) : null}
              data={drawers.map((drawer) => ({ value: String(drawer.id), label: drawer.name }))}
              onChange={(next) => {
                setSelectedDrawerId(next);
                setOwnershipRequested(null);
              }}
              disabled={isSaving}
              allowDeselect={false}
            />
            {selectedDrawer?.cashDrawerOwnerStationName && (
              <Text size="sm">עמדה אחראית: {selectedDrawer.cashDrawerOwnerStationName}</Text>
            )}
            <Switch
              label="בעלות על המגירה"
              checked={wantsOwnership}
              onChange={(event) => setOwnershipRequested(event.currentTarget.checked)}
              disabled={isSaving || !selectedDrawer || !deviceId || (!normalized && !ownsDrawer)}
            />
          </>
        )}

        <div className={classes.actions}>
          <Button variant="cancel" onClick={onClose} disabled={isSaving} dataTestId="stationary-setup-cancel">
            ביטול
          </Button>
          <Button
            variant="save"
            onClick={() => onSave(
              normalized.length > 0 ? normalized : null,
              ownershipDirty && selectedDrawer
                ? {
                  printerId: selectedDrawer.id,
                  stationName: wantsOwnership && normalized ? normalized : null,
                  ...(wantsOwnership && !ownsDrawer ? { takeOwnership: true as const } : {}),
                }
                : undefined,
            )}
            disabled={isSaving || !isDirty}
            loading={isSaving}
            dataTestId="stationary-setup-save"
          >
            שמור
          </Button>
        </div>
      </div>
    </Modal>
  );
};
